/**
 * @file SocialSignIn.tsx
 * @brief Google, Apple and Microsoft buttons on the WEBPANEL sign-in page.
 *
 * @details
 * Both return an identity token and call the same backend routes as the
 * phone. Google's button is the official one. Apple uses the popup flow;
 * its Services ID is a separate Apple client from the iPhone bundle id.
 *
 * Inside the desktop app (DESKTOP/, Electron) Google's popup button cannot
 * work: Google refuses its sign-in pages in embedded browsers, and a popup
 * opened in the real browser has no way back to the app. There the button is
 * ours, and the desktop shell signs in through the user's real browser and
 * hands back the same ID token (30-09-2026).
 *
 * Microsoft (06-10-2026): Microsoft 365 company accounts + personal Outlook,
 * a popup with code + PKCE (microsoftSignIn.ts); shown only once
 * VITE_MICROSOFT_CLIENT_ID is set. The desktop app allows its popup.
 *
 * @author DES
 * @date 30-09-2026
 */

import { useEffect, useRef, useState } from "react";
import { LoaderCircle } from "lucide-react";
import { ApiClientError } from "@/shared/api/client";
import { env } from "@/shared/config/env";
import { useAuth, type SignInOptions } from "@/shared/auth/auth-context";
import type { AuthUser } from "@/shared/types/api";
import { Button } from "@/components/ui/Button";
import { desktopShell } from "@/shared/desktop/desktopShell";
import { useTheme } from "@/shared/theme/theme-context";
import { signInWithMicrosoftPopup } from "./microsoftSignIn";

type GoogleCredential = { credential?: string };

interface GoogleId {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredential) => void;
    cancel_on_tap_outside?: boolean;
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: Record<string, string | number | boolean>,
  ) => void;
}

interface AppleAuth {
  init: (config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    usePopup: boolean;
  }) => void;
  signIn: () => Promise<AppleResult>;
  renderButton?: () => void;
}

interface AppleResult {
  authorization?: { id_token?: string };
  user?: { name?: { firstName?: string; lastName?: string } };
}

/** One button width/height for Google, Apple, Microsoft (W23, 25-10-2026). */
const BUTTON_HEIGHT = 40; // Google's "large" button is fixed at 40px.
const BUTTON_MAX_WIDTH = 375; // Apple's maximum; Google allows 400.

declare global {
  interface Window {
    google?: { accounts: { id: GoogleId } };
    AppleID?: { auth: AppleAuth };
  }
}

function loadScript(src: string): Promise<void> {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    return existing.getAttribute("data-loaded") === "1"
      ? Promise.resolve()
      : new Promise((resolve, reject) => {
          existing.addEventListener("load", () => resolve(), { once: true });
          existing.addEventListener("error", () => reject(new Error("script")), { once: true });
        });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.setAttribute("data-loaded", "1");
      resolve();
    };
    script.onerror = () => reject(new Error("script"));
    document.head.appendChild(script);
  });
}

/** Signing in again from a delete screen (B33): see `SignInOptions.reauth`. */
const REAUTH: SignInOptions = { reauth: true };

export function SocialSignIn({
  disabled,
  onSignedIn,
  onError,
  reauth = false,
}: {
  disabled: boolean;
  onSignedIn: (user: AuthUser) => void;
  onError: (err: unknown) => void;
  /** True on a delete screen: no loader, the session stays on failure. */
  reauth?: boolean;
}) {
  const { loginWithGoogle, loginWithApple, loginWithMicrosoft } = useAuth();
  const signInOpts = reauth ? REAUTH : undefined;
  const { resolvedTheme } = useTheme();
  const googleHost = useRef<HTMLDivElement>(null);
  const appleHost = useRef<HTMLDivElement>(null);
  const [appleBusy, setAppleBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [microsoftBusy, setMicrosoftBusy] = useState(false);
  const desktopGoogle = desktopShell()?.signInWithGoogle;

  const onDesktopGoogle = async () => {
    if (!desktopGoogle) return;
    setGoogleBusy(true);
    try {
      const idToken = await desktopGoogle();
      onSignedIn(await loginWithGoogle(idToken, signInOpts));
    } catch (err: unknown) {
      // A server answer (e.g. the two-step verification screen, B31) goes up
      // as is; only the Electron IPC errors need their prefix trimmed.
      if (err instanceof ApiClientError) {
        onError(err);
        return;
      }
      // Electron prefixes IPC errors ("Error invoking remote method…: Error: X").
      const message = err instanceof Error ? err.message : String(err);
      if (message.endsWith("CANCELLED")) return;
      onError(
        new Error(message.replace(/^Error invoking remote method '[^']+': (Error: )?/, "")),
      );
    } finally {
      setGoogleBusy(false);
    }
  };

  useEffect(() => {
    // The desktop app renders its own button (above); no Google script there.
    if (desktopGoogle) return;
    const clientId = env.googleWebClientId;
    const host = googleHost.current;
    if (!clientId || !host) return;
    let cancelled = false;

    void loadScript("https://accounts.google.com/gsi/client")
      .then(() => {
        const googleId = window.google?.accounts.id;
        if (cancelled || !googleId || !googleHost.current) return;
        googleId.initialize({
          client_id: clientId,
          cancel_on_tap_outside: true,
          callback: (response) => {
            const idToken = response.credential;
            if (!idToken) return;
            void loginWithGoogle(idToken, signInOpts)
              .then(onSignedIn)
              .catch((err: unknown) => {
                onError(err);
              });
          },
        });
        googleHost.current.innerHTML = "";
        googleId.renderButton(googleHost.current, {
          type: "standard",
          // Google's own dark-mode button; its iframe is transparent only
          // while the host is `color-scheme: normal` (see the JSX below).
          theme: resolvedTheme === "dark" ? "filled_black" : "outline",
          size: "large",
          text: "continue_with",
          shape: "pill",
          width: Math.min(Math.max(googleHost.current.offsetWidth, 280), BUTTON_MAX_WIDTH),
          logo_alignment: "center",
        });
      })
      .catch(() => {
        if (!cancelled) onError(new Error("Google sign-in could not load."));
      });

    return () => {
      cancelled = true;
    };
  }, [desktopGoogle, loginWithGoogle, onError, onSignedIn, resolvedTheme, signInOpts]);

  // Apple's own button (rendered by Apple's script, so the logo, wording and
  // style follow Apple's rules). Its click opens Apple's popup and reports back
  // through two document events; the backend call is the same as before.
  const finishApple = async (result: AppleResult) => {
    setAppleBusy(true);
    try {
      const identityToken = result.authorization?.id_token;
      if (!identityToken) return;
      const user = await loginWithApple({
        identityToken,
        firstName: result.user?.name?.firstName,
        lastName: result.user?.name?.lastName,
      }, signInOpts);
      onSignedIn(user);
    } catch (err: unknown) {
      onError(err instanceof Error ? err : new Error("Apple sign-in failed"));
    } finally {
      setAppleBusy(false);
    }
  };

  useEffect(() => {
    if (!env.appleServicesId) return;
    let cancelled = false;
    const onSuccess = (event: Event) => {
      void finishApple((event as CustomEvent<AppleResult>).detail ?? {});
    };
    const onFailure = (event: Event) => {
      const detail = (event as CustomEvent<{ error?: string }>).detail;
      const code = detail?.error ?? "";
      if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") return;
      onError(new Error("Apple sign-in failed"));
    };
    document.addEventListener("AppleIDSignInOnSuccess", onSuccess);
    document.addEventListener("AppleIDSignInOnFailure", onFailure);

    void loadScript(
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
    )
      .then(() => {
        const apple = window.AppleID?.auth;
        if (cancelled || !apple || !appleHost.current) return;
        apple.init({
          clientId: env.appleServicesId,
          scope: "name email",
          redirectURI: window.location.origin,
          usePopup: true,
        });
        // Same width as Google's button: Apple sizes its button in pixels.
        const width = Math.min(
          Math.max(appleHost.current.parentElement?.offsetWidth ?? 0, 280),
          BUTTON_MAX_WIDTH,
        );
        appleHost.current.setAttribute("data-width", String(width));
        appleHost.current.style.width = `${width}px`;
        apple.renderButton?.();
      })
      .catch(() => {
        if (!cancelled) onError(new Error("Apple sign-in could not load."));
      });

    return () => {
      cancelled = true;
      document.removeEventListener("AppleIDSignInOnSuccess", onSuccess);
      document.removeEventListener("AppleIDSignInOnFailure", onFailure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- finishApple only reads stable auth callbacks
  }, [loginWithApple, onError, onSignedIn, resolvedTheme]);

  const onMicrosoft = async () => {
    setMicrosoftBusy(true);
    try {
      const idToken = await signInWithMicrosoftPopup(env.microsoftClientId);
      onSignedIn(await loginWithMicrosoft(idToken, signInOpts));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      if (message === "CANCELLED") return;
      onError(err instanceof Error ? err : new Error("Microsoft sign-in failed"));
    } finally {
      setMicrosoftBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {desktopGoogle ? (
        <Button
          type="button"
          variant="ghost"
          // White pill, no border (DES 01-10-2026: "buttons ... border kiyoon hain").
          className="h-10 w-full gap-2 rounded-full bg-app-card text-[15px] font-semibold hover:bg-app-quaternary"
          disabled={disabled || googleBusy}
          onClick={() => void onDesktopGoogle()}
        >
          {googleBusy ? (
            <>
              <LoaderCircle className="h-4 w-4 animate-spin" />
              Finish signing in in your browser
            </>
          ) : (
            <>
              <GoogleMark />
              Continue with Google
            </>
          )}
        </Button>
      ) : (
        <div
          ref={googleHost}
          // `normal` keeps Google's iframe transparent on our dark page.
          style={{ colorScheme: "normal" }}
          className={`flex min-h-10 justify-center ${disabled ? "pointer-events-none opacity-60" : ""}`}
        />
      )}
      {env.appleServicesId ? (
        <div
          className={`flex justify-center ${disabled || appleBusy ? "pointer-events-none opacity-60" : ""}`}
        >
          {/* Apple: black on light, white with an outline on dark. 50 = pill. */}
          <div
            key={resolvedTheme}
            ref={appleHost}
            id="appleid-signin"
            style={{ height: BUTTON_HEIGHT }}
            data-mode="center-align"
            data-type="continue"
            data-color={resolvedTheme === "dark" ? "white" : "black"}
            data-border={resolvedTheme === "dark" ? "true" : "false"}
            data-border-radius="50"
            data-height={BUTTON_HEIGHT}
          />
        </div>
      ) : null}
      {env.microsoftClientId ? (
        <Button
          type="button"
          variant="ghost"
          className="h-10 w-full gap-2 rounded-full bg-app-card text-[15px] font-semibold hover:bg-app-quaternary"
          disabled={disabled || microsoftBusy}
          onClick={() => void onMicrosoft()}
        >
          {microsoftBusy ? (
            <LoaderCircle className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <MicrosoftMark />
              Continue with Microsoft
            </>
          )}
        </Button>
      ) : null}
    </div>
  );
}

/** Google's "G", per Google's sign-in branding guidelines. */
function GoogleMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 48 48" className="h-[18px] w-[18px]">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/** Microsoft's four squares, per Microsoft's sign-in branding guidelines. */
function MicrosoftMark() {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 21 21">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
